import { Injectable } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UserNotificationItem } from '../model/userNotificationItem';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Environment } from '../environment/environment';
import { plainToClass } from 'class-transformer';
import { map } from 'rxjs/operators';
import { Response } from '../model/response';
import { id, TreeMapModule } from '@swimlane/ngx-charts';

@Injectable({
  providedIn: 'root'
})
export class UserNotificationService {
  menuTrigger: MatMenuTrigger;
  data: Array<UserNotificationItem>=[];
  // Cache da LISTA COMPLETA (lidas + não-lidas) — populado sempre que
  // `getUserNotifications()` roda. Usado pelo NotificationsCenterComponent
  // (dialog do sino) pra renderizar imediatamente ao abrir, sem esperar
  // HTTP. `warmAllCache()` faz o pré-carregamento silencioso.
  allData: Array<UserNotificationItem>=[];
  
  private messageStatusChangeTriggerSource = new Subject<void>(); // Event emitted from notifications page so that the menu update its content
  messageStatusChangeTrigger$ = this.messageStatusChangeTriggerSource.asObservable();

  private messagesCleanedTriggerSource = new Subject<void>(); // Event emitted from menu so that the notifications page update its content
  messagesCleanedTrigger$ = this.messagesCleanedTriggerSource.asObservable();
  constructor(
    private http: HttpClient
  ) { }

  
    clearAllNotifications(): Observable<void> {
      return this.markAllMessagesAsRead().pipe(
        map(()=> {
        this.data = [];
      }));
    }
  
    // Contagem TOTAL de não lidas — vem do endpoint /byUser/ (backend
    // retorna `{items, unreadCount}`). Usado pelo badge do sino pra mostrar
    // o número real (não capado pelo `size` da lista).
    unreadCount: number = 0;

    refreshNotifications(): Observable<UserNotificationItem[]> {
      return this.getUserLatestNotifications().pipe(
        map(notifications => {
          this.data = notifications;
          return this.data;
        })
      )
    }

    getUserLatestNotifications(): Observable<UserNotificationItem[]>{ // Get the most recent (few ones)
      return this.http.get<Response>(Environment.urls.API + '/core/email/byUser/')
            .pipe(map(v => {
              // Backend agora retorna `{items, unreadCount}` em vez de
              // array puro. Fallback pro shape antigo (array) mantém
              // compat se o backend for pinado numa versão mais velha.
              const payload = v.data as any;
              const rawItems = Array.isArray(payload) ? payload : (payload?.items ?? []);
              this.unreadCount = Array.isArray(payload)
                ? rawItems.length
                : (payload?.unreadCount ?? rawItems.length);
              const items = plainToClass(UserNotificationItem, rawItems as Array<UserNotificationItem>);
              items.forEach(item =>{
                item.cc = this.formatCc(item.cc)
              })
              return items;
            }))
    }

    getUserNotifications(): Observable<UserNotificationItem[]>{ // Get ALL the user notifications
      return this.http.get<Response>(Environment.urls.API + '/core/email/byUser/all/')
            .pipe(map(v =>{
              const items = plainToClass(UserNotificationItem, v.data as Array<UserNotificationItem>);
              items.forEach(item =>{
                item.cc = this.formatCc(item.cc)
              })
              // Cacheia a lista completa pra abertura instantânea do
              // dialog do sino. `allData` é lido pelo
              // NotificationsCenterComponent no ngOnInit.
              this.allData = items;
              return items;
            }))
    }

    /** Warm-up silencioso do cache `allData`. Chamado pelo sino após
     *  refresh do count — assim quando o usuário abre o dialog o cache
     *  já está pronto. Erros são ignorados (é só cache). */
    warmAllCache(): void {
      this.getUserNotifications().subscribe({ error: () => {} });
    }

    getEmailContent(id: number): Observable<string>{ // Get ALL the user notifications
      return this.http.get<Response>(Environment.urls.API + '/core/email/byUser/'+id+'/')
            .pipe(map(v => v.data as string))
    }

    markAllMessagesAsRead(): Observable<any>{
      return this.http.post<Response>(Environment.urls.API + '/core/email/byUser/all/', {})
    }

    /** Bulk: marca todas as mensagens do usuário como NÃO lidas (uma
     *  UPDATE SQL só, feito no backend). */
    markAllMessagesAsUnread(): Observable<any>{
      return this.http.post<Response>(Environment.urls.API + '/core/email/byUser/all/', {
        status_change: false
      })
    }

    changeMessageReadStatus(id: Number, status: boolean): Observable<any>{
      return this.http.post<Response>(Environment.urls.API + '/core/email/byUser/all/', {
        notification_id: id,
        status_change: status
      })
    }

    emitMessageStatusChangeEvent(){
      this.messageStatusChangeTriggerSource.next();
    }

    emitMessagesCleanedEvent(){
      this.messagesCleanedTriggerSource.next();
    }

    formatCc(cc: string): string{
      // Remove "[" and "]" from the cc string and remove simple quotes from the adrresses
      if(cc.startsWith("[") && cc.endsWith("]")){
        cc = cc.substring(1, cc.length-1).replace("'","");
      }
      // Else, email's cc still the same
      return cc
    }
}
